import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { POST } from '../../config/api';
import { setTokens } from '../../helpers/tokenStorage';
import { routes } from '../../config/config';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import styles from './LoginWithEmailScreen.module.css';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LoginWithEmailScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const enteredEmail = location.state?.email || '';

  const [email, setEmail] = useState(enteredEmail);
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (buttonDisabled || isSubmitting) return;

    setIsSubmitting(true);
    setError('');

    const response = await POST('/auth/login', {}, {
      email,
      password,
    });

    setIsSubmitting(false);

    if (response.status === 200 && response.data) {
      if (response.data.message === 'Code sent') {
        // Navigate to OTP screen for email verification
        navigate(routes.otp, { state: { email } });
        return;
      }

      // Successful login
      const { accessToken, refreshToken, user, categories } = response.data;

      // Store tokens
      await setTokens(accessToken, refreshToken);

      // Store user data in localStorage
      if (user) {
        const userData = { ...user };
        delete userData.chats;
        localStorage.setItem('tellmiz_user', JSON.stringify(userData));
        if (user.unreadMessagesCount !== undefined) {
          localStorage.setItem('tellmiz_unreadMessagesCount', user.unreadMessagesCount.toString());
        }
        if (user.chats) {
          localStorage.setItem('tellmiz_chats', JSON.stringify(user.chats));
        }
      }

      if (categories) {
        localStorage.setItem('tellmiz_categories', JSON.stringify(categories));
      }

      // Navigate to reels screen
      navigate(routes.reels);
    } else if (response.error) {
      if (response.status === 401) {
        setError('Incorrect email or password');
      } else {
        setError(response.message || 'Unable to login. Please try again later.');
      }
    } else {
      setError('Unable to login. Please try again later.');
    }
  };

  const handleForgotPassword = () => {
    navigate(routes.forgotPassword);
  };

  useEffect(() => {
    if (emailRegex.test(email) && password.length >= 6) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
  }, [email, password]);

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowBackIcon className={styles.backIcon} />
        </button>
        <h1 className={styles.title}>Login</h1>
      </div>

      <div className={styles.content}>
        <form onSubmit={handleLogin} className={styles.form}>
          <div className={styles.inputContainer}>
            <label className={styles.inputLabel}>Email</label>
            <input
              type="email"
              className={styles.textInput}
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
              autoComplete="email"
              required
            />
          </div>

          <div className={styles.inputContainer}>
            <label className={styles.inputLabel}>Password</label>
            <div className={styles.passwordContainer}>
              <input
                type={passwordVisible ? 'text' : 'password'}
                className={styles.passwordInput}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className={styles.eyeButton}
                onClick={() => setPasswordVisible(!passwordVisible)}
              >
                {passwordVisible ? (
                  <VisibilityOffIcon className={styles.eyeIcon} />
                ) : (
                  <VisibilityIcon className={styles.eyeIcon} />
                )}
              </button>
            </div>
          </div>

          <button
            type="button"
            className={styles.forgotPassword}
            onClick={handleForgotPassword}
          >
            Forgot password?
          </button>

          {error && <div className={styles.errorMessage}>{error}</div>}

          <div className={styles.terms}>
            <p>
              By continuing, you agree to our{' '}
              <a
                href="https://tellmiz.com/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                terms of service
              </a>{' '}
              and{' '}
              <a
                href="https://tellmiz.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                privacy policy
              </a>
              .
            </p>
          </div>

          <button
            type="submit"
            className={`${styles.loginButton} ${isSubmitting || buttonDisabled ? styles.disabled : ''}`}
            disabled={isSubmitting || buttonDisabled}
          >
            {isSubmitting ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginWithEmailScreen;

